import { Video, MapPin, Calendar, Clock, ExternalLink, Copy, Check, Send, Plus, X, Users, Loader2, Pencil, Save, Ban, RefreshCw } from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondInterviewDialog } from '@/components/SecondInterviewDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatLocalizedDate } from '@/lib/localizedDate';
import {
  canEditF2FInvitation,
  canRescheduleF2FInvitation,
  readEdgeFunctionErrorPayload,
  resolveF2FCancellationUiOutcome,
  shouldHydrateF2FEditDraft,
  type F2FCancellationPayload,
} from '@/lib/forkhire/workflow/f2fCancellationOutcome';
import {
  deliveryResultFromInvoke,
  failedF2FDeliveryRecipients,
  f2fForwardEmailIdempotencyKey,
  readF2FDeliveryErrorPayload,
  resolveF2FRescheduleUiOutcome,
  summarizeF2FDeliveryResults,
  type F2FReschedulePayload,
} from '@/lib/forkhire/workflow/f2fDeliveryOutcome';

export interface F2FDetails {
  interview_type: 'online' | 'face_to_face';
  scheduled_date: string;
  scheduled_time: string;
  meeting_link?: string;
  location_link?: string;
  attendee_emails?: string[];
  f2f_response_status?: string;
  f2f_response_reason_label?: string;
  f2f_response_note?: string;
  f2f_response_at?: string;
}

interface F2FPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateName: string;
  details: F2FDetails | null;
  applicationId?: string;
  jobTitle?: string;
  onDetailsUpdated?: () => void;
}

export function F2FPreviewDialog({ open, onOpenChange, candidateName, details, applicationId, jobTitle, onDetailsUpdated }: F2FPreviewDialogProps) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [forwardEmails, setForwardEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [forwardedRecipients, setForwardedRecipients] = useState<string[]>([]);
  const [showCancelF2f, setShowCancelF2f] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editType, setEditType] = useState<'online' | 'face_to_face'>('online');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editLink, setEditLink] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const existingAttendees = useMemo(() => details?.attendee_emails || [], [details?.attendee_emails]);

  const detailsTargetKey = useMemo(() => {
    if (!details) return '';
    return [
      applicationId ?? '',
      candidateName,
    ].join('|');
  }, [applicationId, candidateName, details]);
  const activeDetailsTargetKeyRef = useRef('');

  // Reset edit state for a new appointment, but never overwrite dirty in-progress edits during refetch.
  useEffect(() => {
    if (!details || !open) return;
    const isNewDialogTarget = activeDetailsTargetKeyRef.current !== detailsTargetKey;
    if (!shouldHydrateF2FEditDraft(isNewDialogTarget, isEditing)) return;

    setEditType(details.interview_type);
    setEditDate(details.scheduled_date);
    setEditTime(details.scheduled_time);
    setEditLink((details.interview_type === 'online' ? details.meeting_link : details.location_link) || '');
    setHasChanges(false);
    setIsEditing(false);
    activeDetailsTargetKeyRef.current = detailsTargetKey;
  }, [details, detailsTargetKey, open, isEditing, hasChanges]);

  // Fetch previously forwarded emails from message_logs
  useEffect(() => {
    if (!applicationId || !open) return;
    const fetchForwarded = async () => {
      const { data } = await supabase
        .from('message_logs')
        .select('to_email')
        .eq('application_id', applicationId)
        .eq('type', 'general')
        .eq('status', 'sent');
      if (data) {
        const candidateEmails = new Set(existingAttendees.map(e => e.toLowerCase()));
        const forwarded = [...new Set(data.map(r => r.to_email.toLowerCase()))]
          .filter(e => !candidateEmails.has(e));
        setForwardedRecipients(forwarded);
      }
    };
    fetchForwarded();
  }, [applicationId, open, existingAttendees]);

  // Track changes
  useEffect(() => {
    if (!details || !isEditing) return;
    const origLink = (details.interview_type === 'online' ? details.meeting_link : details.location_link) || '';
    const changed =
      editType !== details.interview_type ||
      editDate !== details.scheduled_date ||
      editTime !== details.scheduled_time ||
      editLink !== origLink;
    setHasChanges(changed);
  }, [editType, editDate, editTime, editLink, details, isEditing]);

  if (!details) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">{t('hire.f2fPreview.title')}</DialogTitle>
          <DialogDescription className="text-xs">{t('hire.f2fPreview.detailsFor')} <strong>{candidateName}</strong></DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-4 text-center">{t('hire.f2fPreview.empty')}</p>
      </DialogContent>
    </Dialog>
  );

  const isOnline = isEditing ? editType === 'online' : details.interview_type === 'online';
  const link = isEditing ? editLink : (details.interview_type === 'online' ? details.meeting_link : details.location_link);
  const displayDate = isEditing ? editDate : details.scheduled_date;
  const displayTime = isEditing ? editTime : details.scheduled_time;

  let formattedDate = displayDate;
  try {
    formattedDate = formatLocalizedDate(displayDate, 'PPP', i18n.language);
  } catch { /* keep raw */ }

  let formattedTime = displayTime;
  try {
    const [h, m] = displayTime.split(':');
    const d = new Date();
    d.setHours(parseInt(h), parseInt(m));
    formattedTime = formatLocalizedDate(d, 'p', i18n.language);
  } catch { /* keep raw */ }

  const handleCopyLink = () => {
    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      toast({ title: t('hire.f2fPreview.toasts.linkCopied') });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !forwardEmails.includes(email)) {
      setForwardEmails((prev) => [...prev, email]);
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    setForwardEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleForwardSend = async () => {
    if (forwardEmails.length === 0 || !applicationId) return;
    setIsSending(true);
    try {
      const interviewTypeLabel = isOnline ? t('hire.f2fPreview.type.online') : t('hire.f2fPreview.type.physical');
      const linkLabel = isOnline ? t('hire.f2fPreview.labels.meetingLink') : t('hire.f2fPreview.labels.location');

      const promises = forwardEmails.map(async (toEmail) => {
        const bodyHtml = [
          `<p>${t('hire.f2fPreview.email.greeting')}</p>`,
          `<p>${jobTitle
            ? t('hire.f2fPreview.email.introWithJob', { jobTitle })
            : t('hire.f2fPreview.email.intro')}</p>`,
          `<p>${t('hire.f2fPreview.email.candidateLabel')}: <strong>${candidateName}</strong></p>`,
          `<p><strong>${t('hire.f2fPreview.email.detailsHeading')}:</strong></p>`,
          '<ul>',
          `<li>${t('hire.f2fPreview.email.typeLabel')}: ${interviewTypeLabel}</li>`,
          `<li>${t('hire.f2fPreview.email.dateLabel')}: ${formattedDate}</li>`,
          `<li>${t('hire.f2fPreview.email.timeLabel')}: ${formattedTime}</li>`,
          `<li>${linkLabel}: ${link ? `<a href="${link}">${link}</a>` : t('common.notAvailable')}</li>`,
          '</ul>',
          `<p>${t('hire.f2fPreview.email.closing')}</p>`,
        ].join('');
        return supabase.functions.invoke('send-email', {
          body: {
            to_email: toEmail,
            type: 'general',
            application_id: applicationId,
            subject: jobTitle
              ? t('hire.f2fPreview.emailSubjectWithJob', { candidateName, jobTitle })
              : t('hire.f2fPreview.emailSubject', { candidateName }),
            body_html: bodyHtml,
            variables: {
              '{{candidate_name}}': candidateName,
              '{{job_title}}': jobTitle || t('hire.f2fPreview.emailFallbacks.role'),
              '{{company_name}}': t('hire.f2fPreview.emailFallbacks.employer'),
              '{{interview_type}}': interviewTypeLabel,
              '{{scheduled_date}}': formattedDate,
              '{{scheduled_time}}': formattedTime,
              '{{interview_link}}': link || '',
            },
            platform_template_key: 'candidate.f2f_interview_scheduled',
            platform_template_portal_scope: 'forkhire',
            platform_template_audience: 'employer',
            email_idempotency_key: await f2fForwardEmailIdempotencyKey({
              applicationId,
              recipient: toEmail,
              details,
            }),
          },
        });
      });

      const invocationResults = await Promise.all(promises);
      const deliveryResults = await Promise.all(invocationResults.map((result, index) => (
        deliveryResultFromInvoke(forwardEmails[index], result)
      )));
      const summary = summarizeF2FDeliveryResults(deliveryResults);
      const successfulRecipients = summary.deliveries
        .filter((delivery) => delivery.success)
        .map((delivery) => delivery.recipient);
      setForwardedRecipients((current) => [...new Set([...current, ...successfulRecipients])]);

      if (!summary.complete) {
        setForwardEmails(failedF2FDeliveryRecipients(summary));
        toast({
          title: t('hire.f2fPreview.toasts.forwardPartial'),
          description: t('hire.f2fPreview.toasts.forwardPartialDescription', {
            sent: summary.sentCount,
            failed: summary.failedCount,
          }),
          variant: 'destructive',
        });
        return;
      }

      toast({ title: t('hire.f2fPreview.toasts.forwarded'), description: t('hire.f2fPreview.toasts.sentToRecipients', { count: summary.sentCount }) });
      setForwardEmails([]);
      setEmailInput('');
      setShowForward(false);
    } catch (err) {
      toast({ title: t('hire.f2fPreview.toasts.forwardFailed'), description: t('common.tryAgain'), variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveAndNotify = async () => {
    if (!applicationId || !hasChanges) return;
    setIsSaving(true);
    try {
      const updatedDetails: F2FDetails = {
        interview_type: editType,
        scheduled_date: editDate,
        scheduled_time: editTime,
        ...(editType === 'online' ? { meeting_link: editLink } : { location_link: editLink }),
        attendee_emails: existingAttendees,
      };

      const { data, error } = await supabase.functions.invoke('f2f-schedule', {
        body: {
          action: 'reschedule',
          application_id: applicationId,
          details: updatedDetails,
          additional_notify_emails: forwardedRecipients,
        },
      });
      const payload = (error
        ? await readF2FDeliveryErrorPayload(error)
        : data) as F2FReschedulePayload | null;
      const outcome = resolveF2FRescheduleUiOutcome(payload);
      if (outcome === 'failed') throw error || new Error(payload?.error || 'F2F update failed');
      if (outcome === 'saved_notification_failed') {
        toast({
          title: t('hire.f2fPreview.toasts.savedNotificationFailed'),
          description: payload?.error || t('hire.f2fPreview.toasts.savedNotificationFailedDescription'),
          variant: 'destructive',
        });
        return;
      }

      toast({ title: t('hire.f2fPreview.toasts.updated'), description: t('hire.f2fPreview.toasts.updatedDescription') });
      setIsEditing(false);
      setHasChanges(false);
      onDetailsUpdated?.();
    } catch (err) {
      console.error('Save & notify error:', err);
      toast({ title: t('hire.f2fPreview.toasts.saveFailed'), description: t('common.tryAgain'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (details) {
      setEditType(details.interview_type);
      setEditDate(details.scheduled_date);
      setEditTime(details.scheduled_time);
      setEditLink((details.interview_type === 'online' ? details.meeting_link : details.location_link) || '');
      activeDetailsTargetKeyRef.current = detailsTargetKey;
    }
    setIsEditing(false);
    setHasChanges(false);
  };

  const hasForwardDraft = showForward && (forwardEmails.length > 0 || !!emailInput.trim());
  const hasCancelDraft = showCancelF2f && (!!cancelReason || !!cancelNote.trim());
  const hasUnsavedLocalDraft = (isEditing && hasChanges) || hasForwardDraft || hasCancelDraft;

  const resetTransientDrafts = () => {
    handleCancelEdit();
    setShowForward(false);
    setForwardEmails([]);
    setEmailInput('');
    setShowCancelF2f(false);
    setCancelReason('');
    setCancelNote('');
  };

  const requestClose = () => {
    if (isSaving || isSending || isCancelling || isRescheduling) return;
    if (hasUnsavedLocalDraft) {
      setDiscardConfirmOpen(true);
      return;
    }
    resetTransientDrafts();
    onOpenChange(false);
  };

  const hasOpenChildDialog = showConfirm || discardConfirmOpen || showCancelF2f || showReschedule;
  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (hasOpenChildDialog) return;
    requestClose();
  };

  const discardAndClose = () => {
    resetTransientDrafts();
    setDiscardConfirmOpen(false);
    onOpenChange(false);
  };

  const handleCancelF2f = async () => {
    if (!applicationId || !cancelReason) return;
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('f2f-schedule', {
        body: {
          action: 'cancel',
          application_id: applicationId,
          reason: cancelReason,
          note: cancelNote.trim() || null,
        },
      });
      const payload = (error
        ? await readEdgeFunctionErrorPayload(error)
        : data) as F2FCancellationPayload | null;
      const outcome = resolveF2FCancellationUiOutcome(payload);
      if (outcome === 'failed') throw error || new Error(payload?.error || 'F2F cancellation failed');
      if (outcome === 'cancelled_notification_failed') {
        toast({
          title: t('hire.f2fPreview.toasts.cancelledNotificationFailed'),
          description: payload?.error || t('hire.f2fPreview.toasts.cancelledNotificationFailedDescription'),
          variant: 'destructive',
        });
        onDetailsUpdated?.();
        return;
      }

      toast({ title: t('hire.f2fPreview.toasts.cancelled'), description: t('hire.f2fPreview.toasts.candidateNotified') });
      setShowCancelF2f(false);
      setCancelReason('');
      setCancelNote('');
      onOpenChange(false);
      onDetailsUpdated?.();
    } catch (err) {
      console.error('Cancel F2F error:', err);
      toast({ title: t('hire.f2fPreview.toasts.cancelFailed'), description: t('common.tryAgain'), variant: 'destructive' });
    } finally {
      setIsCancelling(false);
    }
  };

  // Reschedule handler
  const handleReschedule = async (newDetails: {
    interview_type: 'online' | 'face_to_face';
    scheduled_date: string;
    scheduled_time: string;
    meeting_link?: string;
    location_link?: string;
    attendee_emails?: string[];
  }) => {
    if (!applicationId) return;
    setIsRescheduling(true);
    try {
      const { data, error } = await supabase.functions.invoke('f2f-schedule', {
        body: {
          action: 'reschedule',
          application_id: applicationId,
          details: newDetails,
        },
      });
      const payload = (error
        ? await readF2FDeliveryErrorPayload(error)
        : data) as F2FReschedulePayload | null;
      const outcome = resolveF2FRescheduleUiOutcome(payload);
      if (outcome === 'failed') throw error || new Error(payload?.error || 'F2F reschedule failed');
      if (outcome === 'saved_notification_failed') {
        toast({
          title: t('hire.f2fPreview.toasts.savedNotificationFailed'),
          description: payload?.error || t('hire.f2fPreview.toasts.savedNotificationFailedDescription'),
          variant: 'destructive',
        });
        return;
      }

      toast({ title: t('hire.f2fPreview.toasts.rescheduled'), description: t('hire.f2fPreview.toasts.rescheduledDescription') });
      setShowReschedule(false);
      onOpenChange(false);
      onDetailsUpdated?.();
    } catch (err) {
      console.error('Reschedule error:', err);
      toast({ title: t('hire.f2fPreview.toasts.rescheduleFailed'), description: t('common.tryAgain'), variant: 'destructive' });
    } finally {
      setIsRescheduling(false);
    }
  };

  // Check if F2F has been declined or cancelled
  const f2fResponseStatus = details.f2f_response_status;
  const isF2fActionable = canEditF2FInvitation(f2fResponseStatus);
  const isF2fReschedulable = canRescheduleF2FInvitation(f2fResponseStatus);
  const f2fResponseAt = details.f2f_response_at;
  let formattedResponseAt = f2fResponseAt || '';
  try {
    if (f2fResponseAt) {
      formattedResponseAt = formatLocalizedDate(f2fResponseAt, 'PP p', i18n.language);
    }
  } catch { /* keep raw */ }

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
        <DialogHeader className="pe-10">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-base">{t('hire.f2fPreview.title')}</DialogTitle>
            {f2fResponseStatus === 'declined_by_candidate' && (
              <Badge variant="destructive" className="text-[10px]">{t('hire.f2fPreview.status.declinedByCandidate')}</Badge>
            )}
            {f2fResponseStatus === 'cancelled_by_employer' && (
              <Badge variant="secondary" className="text-[10px]">{t('hire.f2fPreview.status.cancelled')}</Badge>
            )}
          </div>
          <DialogDescription className="text-xs">
            {t('hire.f2fPreview.invitationSentTo')} <strong>{candidateName}</strong>
          </DialogDescription>
          {!isEditing && applicationId && isF2fActionable && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 shrink-0 w-fit mt-1" onClick={() => setIsEditing(true)}>
              <Pencil className="w-3.5 h-3.5" /> {t('common.edit')}
            </Button>
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              {isOnline ? <Video className="w-5 h-5 text-primary" /> : <MapPin className="w-5 h-5 text-primary" />}
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">{t('hire.f2fPreview.labels.interviewType')}</p>
              {isEditing ? (
                <Select value={editType} onValueChange={(v) => setEditType(v as 'online' | 'face_to_face')}>
                  <SelectTrigger className="h-8 text-xs mt-1 w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">{t('hire.f2fPreview.type.online')}</SelectItem>
                    <SelectItem value="face_to_face">{t('hire.f2fPreview.type.physical')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="secondary" className="mt-0.5 text-xs">
                  {isOnline ? t('hire.f2fPreview.type.online') : t('hire.f2fPreview.type.physical')}
                </Badge>
              )}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{t('hire.f2fPreview.labels.date')}</span>
              </div>
              {isEditing ? (
                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="h-8 text-xs" />
              ) : (
                <p className="text-sm font-medium text-foreground">{formattedDate}</p>
              )}
            </div>
            <div className="p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{t('hire.f2fPreview.labels.time')}</span>
              </div>
              {isEditing ? (
                <Input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} className="h-8 text-xs" />
              ) : (
                <p className="text-sm font-medium text-foreground">{formattedTime}</p>
              )}
            </div>
          </div>

          {/* Link */}
          <div className="p-3 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center gap-2 mb-1.5">
              {isOnline ? <Video className="w-4 h-4 text-muted-foreground" /> : <MapPin className="w-4 h-4 text-muted-foreground" />}
              <span className="text-xs text-muted-foreground">{isOnline ? t('hire.f2fPreview.labels.meetingLink') : t('hire.f2fPreview.labels.locationLink')}</span>
            </div>
            {isEditing ? (
              <Input
                type="url"
                placeholder={isOnline ? 'https://meet.google.com/...' : 'https://maps.google.com/...'}
                value={editLink}
                onChange={(e) => setEditLink(e.target.value)}
                className="h-8 text-xs font-mono"
              />
            ) : link ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-foreground truncate flex-1 font-mono">{link}</p>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopyLink}>
                    {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">{t('hire.f2fPreview.noLink')}</p>
            )}
          </div>

          {/* Edit mode notice */}
          {isEditing && hasChanges && (
            <div className="p-3 rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-900/10">
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                {t('hire.f2fPreview.editNotice')}
              </p>
            </div>
          )}

          {/* Existing attendees */}
          {existingAttendees.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium">{t('hire.f2fPreview.alsoInvited')}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {existingAttendees.map((email) => (
                  <span key={email} className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    {email}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Forward section */}
          {showForward && !isEditing && (
            <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/5">
              <Label className="text-xs font-medium">{t('hire.f2fPreview.forwardToAdditionalEmails')}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="email@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  className="h-8 text-xs"
                />
                <Button type="button" variant="outline" size="sm" className="h-8 px-2 shrink-0" onClick={addEmail}>
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              {forwardEmails.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {forwardEmails.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                      {email}
                      <button type="button" onClick={() => removeEmail(email)} className="hover:text-destructive transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setShowForward(false); setForwardEmails([]); setEmailInput(''); }}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" className="h-7 text-xs" disabled={forwardEmails.length === 0 || isSending} onClick={handleForwardSend}>
                  {isSending ? <Loader2 className="w-3.5 h-3.5 me-1 animate-spin" /> : <Send className="w-3.5 h-3.5 me-1" />}
                  {t('hire.f2fPreview.sendCount', { count: forwardEmails.length })}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* F2F Response Details (if declined or cancelled) */}
        {f2fResponseStatus && (
          <div className={`p-3 rounded-lg border ${f2fResponseStatus === 'declined_by_candidate' ? 'border-destructive/30 bg-destructive/5' : 'border-muted bg-muted/30'}`}>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              {f2fResponseStatus === 'declined_by_candidate' ? t('hire.f2fPreview.response.declineReason') : t('hire.f2fPreview.response.cancellationReason')}
            </p>
            <p className="text-sm font-medium text-foreground">{details.f2f_response_reason_label}</p>
            {details.f2f_response_note && (
              <p className="text-sm text-muted-foreground mt-1">{details.f2f_response_note}</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {formattedResponseAt}
            </p>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex justify-between pt-2">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" className="text-xs" onClick={handleCancelEdit}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                className="text-xs gap-1.5"
                disabled={!hasChanges || isSaving}
                onClick={() => setShowConfirm(true)}
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {t('hire.f2fPreview.actions.saveNotify')}
              </Button>
            </>
          ) : (
            <>
               <div className="flex gap-2">
                {!showForward && applicationId && isF2fActionable && (
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowForward(true)}>
                    <Send className="w-3.5 h-3.5 me-1.5" /> {t('hire.f2fPreview.actions.forward')}
                  </Button>
                )}
                {applicationId && isF2fActionable && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-destructive/30 text-destructive hover:bg-destructive/5"
                    onClick={() => setShowCancelF2f(true)}
                  >
                    <Ban className="w-3.5 h-3.5 me-1" /> {t('hire.f2fPreview.actions.cancelInterview')}
                  </Button>
                )}
                {applicationId && isF2fReschedulable && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-primary/30 text-primary hover:bg-primary/5"
                    onClick={() => setShowReschedule(true)}
                  >
                    <RefreshCw className="w-3.5 h-3.5 me-1" /> {t('hire.f2fPreview.actions.rescheduleInterview')}
                  </Button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={requestClose}>{t('common.close')}</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Confirmation dialog for edit save */}
    <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{t('hire.f2fPreview.confirmUpdate.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-relaxed">
            {t('hire.f2fPreview.confirmUpdate.description')}
            <ul className="mt-2 space-y-1 list-disc list-inside text-muted-foreground">
              <li><strong>{candidateName}</strong> ({t('hire.f2fPreview.confirmUpdate.candidate')})</li>
              {existingAttendees.length > 0 && (
                <li>{t('hire.f2fPreview.confirmUpdate.attendees', { count: existingAttendees.length })}</li>
              )}
              {forwardedRecipients.length > 0 && (
                <li>{t('hire.f2fPreview.confirmUpdate.forwarded', { count: forwardedRecipients.length })}</li>
              )}
            </ul>
            <span className="block mt-2">{t('hire.f2fPreview.confirmUpdate.inAppNotice')}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-xs">{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction className="text-xs gap-1.5" onClick={() => { setShowConfirm(false); handleSaveAndNotify(); }}>
            <Send className="w-3.5 h-3.5" /> {t('hire.f2fPreview.actions.saveNotify')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{t('hire.f2fPreview.discardDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            {t('hire.f2fPreview.discardDialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-xs">{t('hire.f2fPreview.discardDialog.keepEditing')}</AlertDialogCancel>
          <AlertDialogAction className="text-xs" onClick={discardAndClose}>
            {t('hire.f2fPreview.discardDialog.discard')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Cancel F2F Confirmation */}
    <AlertDialog open={showCancelF2f} onOpenChange={setShowCancelF2f}>
      <AlertDialogContent className="max-w-sm">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{t('hire.f2fPreview.cancelDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            {t('hire.f2fPreview.cancelDialog.description')} <strong>{candidateName}</strong>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 py-2">
          <Select value={cancelReason} onValueChange={setCancelReason}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder={t('hire.f2fPreview.cancelDialog.selectReason')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="position_filled">{t('hire.f2fPreview.cancelReasons.positionFilled')}</SelectItem>
              <SelectItem value="rescheduling">{t('hire.f2fPreview.cancelReasons.rescheduling')}</SelectItem>
              <SelectItem value="interview_cancelled">{t('hire.f2fPreview.cancelReasons.interviewCancelled')}</SelectItem>
              <SelectItem value="candidate_mismatch">{t('hire.f2fPreview.cancelReasons.candidateMismatch')}</SelectItem>
              <SelectItem value="internal_decision">{t('hire.f2fPreview.cancelReasons.internalDecision')}</SelectItem>
              <SelectItem value="other">{t('hire.f2fPreview.cancelReasons.other')}</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            placeholder={t('hire.f2fPreview.cancelDialog.notePlaceholder')}
            className="text-sm min-h-[80px]"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="text-xs">{t('hire.f2fPreview.actions.goBack')}</AlertDialogCancel>
          <AlertDialogAction
            className="text-xs bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            disabled={!cancelReason || isCancelling}
            onClick={(e) => { e.preventDefault(); handleCancelF2f(); }}
          >
            {isCancelling ? t('hire.f2fPreview.actions.cancelling') : t('hire.f2fPreview.actions.cancelNotify')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Reschedule F2F Interview */}
    <SecondInterviewDialog
      open={showReschedule}
      onOpenChange={setShowReschedule}
      candidateName={candidateName}
      onSubmit={handleReschedule}
      isPending={isRescheduling}
    />
    </>
  );
}
